import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConditionField, missingConditionFields } from '../../shared/data-sheet-filters.codec';
import { ConditionSubject, TdbOverride, findOperatorFor, operatorsFor } from '../../shared/tcdl-ops';

/**
 * Bedingungszeile eines Modulaufrufs: Subjekt (WERT, OBJEKT, FENSTER), Bezug, Operator und
 * bei WERT der Vergleichswert.
 *
 * <p>Echo-Vertrag wie {@code app-testdaten-block}: Die Zeile haelt keinen eigenen Zustand,
 * meldet jede Eingabe als Patch ueber {@link changed} und zeigt erst den Stand, den der
 * Aufrufer zurueckgibt.
 *
 * <p>Design-Luecke (NEX-1084): Das Bundle zeigt nur das Subjekt WERT. OBJEKT und FENSTER
 * sind im Stil der Wert-Zeile gebaut — Namensfeld plus Auswahl VORHANDEN / NICHT VORHANDEN.
 */
@Component({
  selector: 'app-step-condition-editor',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './step-condition-editor.component.html',
  styleUrl: './step-condition-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepConditionEditorComponent {
  private readonly translate = inject(TranslateService);

  readonly override = input.required<TdbOverride>();
  /** Haengt an data-testid und Screenreader-Label, z. B. der TDB-Key. */
  readonly scopeId = input.required<string>();
  readonly disabled = input(false);

  readonly changed = output<Partial<TdbOverride>>();

  readonly subjects: readonly { readonly value: ConditionSubject; readonly labelKey: string }[] = [
    { value: 'WERT', labelKey: 'tdb.subj.value' },
    { value: 'OBJEKT', labelKey: 'tdb.subj.object' },
    { value: 'FENSTER', labelKey: 'tdb.subj.window' },
  ];

  readonly subj = computed(() => this.override().subj ?? '');
  readonly operators = computed(() => operatorsFor(this.subj()));
  /** Ein Operator, der nicht zum Subjekt passt, zeigt den Platzhalter (bleibt aber im Entwurf). */
  readonly opCode = computed(() => findOperatorFor(this.subj(), this.override().op)?.code ?? '');
  readonly needsValue = computed(() => !!findOperatorFor(this.subj(), this.override().op)?.needsValue);
  readonly refPlaceholderKey = computed(() => {
    if (this.subj() === 'WERT') return 'tdb.ref.placeholder';
    return this.subj() === 'FENSTER' ? 'tdb.ref.placeholderWindow' : 'tdb.ref.placeholderObject';
  });
  readonly missing = computed<readonly ConditionField[]>(() =>
    this.disabled() ? [] : missingConditionFields(this.override()),
  );

  private readonly langChange = toSignal(this.translate.onLangChange, { initialValue: null });
  readonly missingFieldsText = computed(() => {
    this.langChange();
    return this.missing()
      .map((field) => this.translate.instant(`tdb.condition.field.${field}`) as string)
      .join(', ');
  });

  onSubjChange(event: Event): void {
    this.changed.emit({ subj: (event.target as HTMLSelectElement).value as ConditionSubject | '' });
  }

  onRefInput(event: Event): void {
    this.changed.emit({ ref: (event.target as HTMLInputElement).value });
  }

  onOpChange(event: Event): void {
    this.changed.emit({ op: (event.target as HTMLSelectElement).value });
  }

  onValInput(event: Event): void {
    this.changed.emit({ val: (event.target as HTMLInputElement).value });
  }
}
